import React, { useContext, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { PraksisstedbrukerDto } from '../api/generated/praksissted-generated-api';
import translations from './myorganization.psa.i18n';
import { useIntl } from 'react-intl';
import Button from '../components/button';
import {
  Table,
  TableHeader,
  TableMain,
  TableRow,
  TableCellTh,
  TableCellTd,
} from '../components/table/alpTable';
import {
  OnStedHoverCard,
  StedType,
} from '../components/hierarchy/onhoverhierarchyinfo';
import * as css from './myorganization.psa.module.scss';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useLoader } from '../api/useLoader';
import { flattenPraksisstedUndernodeDto } from '../util/psaUtils';

interface PropsPs {
  users: PraksisstedbrukerDto[];
  handleDelete: (id: string, name: string) => void;
}

const UserTablePs: React.FC<React.PropsWithChildren<PropsPs>> = ({
  users,
  handleDelete,
}) => {
  const [activeCol, setActiveCol] = useState<number>(-1);
  const [sortedUsers, setSortedUsers] = useState<PraksisstedbrukerDto[]>();
  const [search, setSearch] = useState<string[]>(['', '', '']);

  const { praksisstedApi } = useContext(PraksisstedApiContext);
  const { data: praksisstederWithAccess } = useLoader(() =>
    praksisstedApi.avdelingerGet(),
  );

  const stederWithAccessIds = praksisstederWithAccess
    ? flattenPraksisstedUndernodeDto(praksisstederWithAccess).map((it) => it.id)
    : [];

  const { formatMessage, locale } = useIntl();
  const tableHeaders = [
    { title: formatMessage({ id: translations.name.id }) },
    { title: formatMessage({ id: translations.status.id }) },
    { title: formatMessage({ id: translations.role.id }) },
    { title: formatMessage({ id: translations.email.id }) },
    { title: formatMessage({ id: translations.affiliation.id }) },
    { title: formatMessage({ id: translations.action.id }) },
  ];

  const filteredAndSortedUsers = React.useMemo(() => {
    const navnSearch = (
      fornavn: string,
      etternavn: string,
      searchIndex: number,
    ) => {
      return (
        `${fornavn} ${etternavn}`.toLowerCase().indexOf(search[searchIndex]) >
        -1
      );
    };

    return (sortedUsers || users).filter((user) => {
      const filterName =
        search[0].length === 0 || navnSearch(user.fornavn, user.etternavn, 0);
      const filterEmail =
        search[1].length === 0 ||
        (user.epost && user.epost.toLowerCase().indexOf(search[1]) > -1);
      const filterPlace =
        search[2].length === 0 ||
        (user.tilhorighet?.praksissted.navn &&
          user.tilhorighet.praksissted.navn.toLowerCase().indexOf(search[2]) >
            -1);

      return filterName && filterEmail && filterPlace;
    });
  }, [sortedUsers, users, search]);

  const getRows = () =>
    filteredAndSortedUsers.map((bruker) => {
      let tabHeader = 0;

      return (
        <TableRow key={bruker.id}>
          <TableCellTd header={tableHeaders[tabHeader++].title}>
            <NavLink
              to={`/myorganization/praksisstedbrukerprofil/${bruker.id}`}
            >
              {bruker.fornavn} {bruker.etternavn}
            </NavLink>
          </TableCellTd>
          <TableCellTd header={tableHeaders[tabHeader++].title}>
            {bruker.status}
          </TableCellTd>
          <TableCellTd header={tableHeaders[tabHeader++].title}>
            {bruker.tilhorighet?.brukerrolle}
          </TableCellTd>
          <TableCellTd header={tableHeaders[tabHeader++].title}>
            {bruker.epost && (
              <a
                target="_top"
                rel="noopener noreferrer"
                href={'mailto:' + bruker.epost}
              >
                {bruker.epost}
              </a>
            )}
          </TableCellTd>
          <TableCellTd header={tableHeaders[tabHeader++].title}>
            {bruker.tilhorighet ? (
              <div className={css.placeName}>
                {bruker.tilhorighet.praksissted.navn}
                {stederWithAccessIds.includes(
                  bruker.tilhorighet.praksissted.id,
                ) && (
                  <OnStedHoverCard
                    id={bruker.tilhorighet.praksissted.id}
                    navn={bruker.tilhorighet.praksissted.navn}
                    sted={StedType.PRAKSISSTED}
                  />
                )}
              </div>
            ) : (
              <></>
            )}
          </TableCellTd>
          <TableCellTd header={tableHeaders[tabHeader].title}>
            {bruker.kanSlette && (
              <Button.Tertiary
                id={'deleteButton_' + bruker.id}
                onClick={() => {
                  handleDelete(
                    bruker.id,
                    bruker.fornavn + ' ' + bruker.etternavn,
                  );
                }}
              >
                {formatMessage({ id: translations.deleteUser.id })}
              </Button.Tertiary>
            )}
          </TableCellTd>
        </TableRow>
      );
    });

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = (
    col: number,
    a: PraksisstedbrukerDto,
    b: PraksisstedbrukerDto,
  ) => {
    switch (col) {
      case 0:
        return compare(a.etternavn, b.etternavn);
      case 1:
        return compare(a.status!, b.status!);
      case 2:
        return compare(
          a.tilhorighet?.brukerrolle || '',
          b.tilhorighet?.brukerrolle || '',
        );
      case 3:
        return compare(a.epost, b.epost);
      case 4:
        return compare(
          a.tilhorighet?.praksissted.navn || '',
          b.tilhorighet?.praksissted.navn || '',
        );
    }
    return 0;
  };

  const sortUsersByActiveCol = (col: number, ascending: boolean) => {
    setSortedUsers(
      [...users].sort((a, b) => {
        return ascending ? sort(col, b, a) : sort(col, a, b);
      }),
    );
  };

  const canSortIndices = [0, 1, 2, 3, 4];
  const canSearchIndices = [0, 3, 4];

  const getHeaders = () =>
    tableHeaders.map(({ title }, index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activeCol}
        setActiveCol={setActiveCol}
        sort={sortUsersByActiveCol}
        canSort={canSortIndices.includes(index)}
        search={(col, value) =>
          setSearch(
            search.map((searchString, i) =>
              canSearchIndices.indexOf(col) === i
                ? value.toLowerCase()
                : searchString,
            ),
          )
        }
        canSearch={canSearchIndices.includes(index)}
      >
        {title}
      </TableCellTh>
    ));

  return (
    <Table>
      <TableHeader>
        <TableRow top>{getHeaders()}</TableRow>
      </TableHeader>
      <TableMain>{getRows()}</TableMain>
    </Table>
  );
};

export default UserTablePs;
